import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import { buildInviteUrl } from '../../lib/invite'
import { Shell } from '../../components/Shell'
import PortalFrame from '../../components/PortalFrame'
import InvitePanel from '../../components/InvitePanel'
import { Field, PcoIcon, Sill, Spinner, ToggleRow, avatarTone, initials } from '../../components/ui'

const ROLE_LABEL = { admin: 'Administrator', reviewer: 'Reviewer', songwriter: 'Songwriter' }

/** The four capability flags, in the order they appear on the row. Reviewers
 *  are the only seat they apply to — an admin never reviews a song. */
const FLAGS = [
  ['can_leave_freetext', 'Notes'],
  ['can_use_canned_responses', 'Canned replies'],
  ['can_set_meetings', 'Meetings'],
  ['can_approve_members', 'Approve people'],
]

/** The seats a church can hand out, in the order they're offered. One list so
 *  the three pickers and the two bits of prose can't drift apart — they used to
 *  be five hand-written pairs and a `role === 'admin' ? … : …` ternary, which
 *  silently called anyone who wasn't an admin "a reviewer".
 *
 *  `songwriter` is here because a church had no way to invite a writer at all:
 *  the seat was rejected by create_invite_code, so a writer's only route in was
 *  finding the church themselves on /join. That left the two halves of the
 *  product unable to meet. */
const SEATS = [
  ['songwriter', 'Songwriter', 'Songwriter — writes and sends songs', 'a songwriter'],
  ['reviewer', 'Reviewer', 'Reviewer — reviews songs', 'a reviewer'],
  ['admin', 'Administrator', 'Administrator — people & setup only', 'an administrator'],
]
const SEAT_PHRASE = Object.fromEntries(SEATS.map(([k, , , phrase]) => [k, phrase]))

/**
 * The way an admin brings someone into the church who isn't in it yet — a
 * writer from the congregation, a reviewer to take songs, or a second admin.
 * Reuses the same invite_codes machinery as circle and cowriter invites: a
 * link/QR that seats the person the moment they redeem it, whether or not they
 * have an account yet. Direct insert into church_members (which "admins add
 * staff" already permits) only works when the admin already knows the person's
 * user id — not true for someone who hasn't signed up.
 */
function StaffInvite({ churchId, churchName }) {
  const { device } = usePlatform()
  const isPhone = device === 'phone'
  const [open, setOpen] = useState(false)

  // Planning Center roster — a big list with an invite button per row, not a
  // pick-one-then-fill-a-form detour. Checked on mount, not gated behind
  // opening the panel, since the closed-state trigger itself needs to know
  // whether to offer a Planning Center button at all.
  const [pcoConnected, setPcoConnected] = useState(null)
  const [picking, setPicking] = useState(false)
  const [roster, setRoster] = useState(null)
  const [rosterErr, setRosterErr] = useState(null)
  // Step one of the picker. Services organises everyone into teams, so the
  // admin narrows to their music teams before a single name is fetched —
  // prayer, cafe and security are excluded by not being ticked, rather than
  // by us guessing from someone's scheduling permission.
  const [teams, setTeams] = useState(null)
  const [chosenTeams, setChosenTeams] = useState([])
  const [teamsErr, setTeamsErr] = useState(null)
  // Client-side. Once you are down to two or three teams this is tens of
  // people, so filtering here is instant and avoids depending on a Services
  // query parameter that isn't documented.
  const [q, setQ] = useState('')
  const [rowSeat, setRowSeat] = useState({})   // person id -> a SEATS key
  const [rowStatus, setRowStatus] = useState({}) // person id -> { busy, done, emailed, url, code, error }
  const [viewing, setViewing] = useState(null) // { name, seat, code } — the invite currently shown in the share dialog
  const shareDlg = useRef(null)

  // showModal() puts it in the top layer — the table it sits over otherwise
  // clips it via overflow, same reasoning as the delete-confirm sheet elsewhere.
  useEffect(() => {
    const d = shareDlg.current
    if (!d) return
    if (viewing && !d.open) d.showModal()
    if (!viewing && d.open) d.close()
  }, [viewing])

  // The manual path — typing a name for someone not in Planning Center, or
  // when the church has no connection at all. Secondary once PCO is
  // connected, but the only option otherwise.
  const [manualOpen, setManualOpen] = useState(false)
  const [role, setRole] = useState('reviewer')
  const [title, setTitle] = useState('')
  const [code, setCode] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    supabase.rpc('church_pco_connected', { p_church_id: churchId }).then(({ data }) => setPcoConnected(!!data))
  }, [churchId])

  const loadTeams = useCallback(async () => {
    setPicking(true); setTeamsErr(null)
    const { data, error } = await supabase.functions
      .invoke('pco-list-team', { body: { church_id: churchId, mode: 'teams' } })
    setPicking(false)
    if (error) return setTeamsErr(readableError(error))
    setTeams(data?.teams ?? [])
  }, [churchId])

  const loadRoster = useCallback(async (teamIds) => {
    setPicking(true); setRosterErr(null)
    const { data, error } = await supabase.functions.invoke('pco-list-team', {
      body: { church_id: churchId, mode: 'roster', team_ids: teamIds },
    })
    setPicking(false)
    if (error) return setRosterErr(readableError(error))
    setRoster(data?.people ?? [])
  }, [churchId])

  // Fires once the panel is open and the Planning Center tab is the active
  // one — covers both "opened straight into it" and "switched to it from
  // manual" without needing two separate call sites. Loads teams, not people:
  // nothing about anyone is fetched until the admin says which teams matter.
  useEffect(() => {
    if (open && pcoConnected && !manualOpen && teams === null && !picking) loadTeams()
  }, [open, pcoConnected, manualOpen, teams, picking, loadTeams])

  const toggleTeam = (id) =>
    setChosenTeams((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]))

  /** Teams grouped under their service type — the Services-native stand-in for
   *  campus, since Services itself has no campus concept. */
  const teamsByType = (teams ?? []).reduce((acc, t) => {
    ;(acc[t.service_type] ||= []).push(t)
    return acc
  }, {})

  const visibleRoster = (roster ?? []).filter((p) => {
    if (!q.trim()) return true
    const hay = `${p.name} ${(p.positions || []).join(' ')}`.toLowerCase()
    return hay.includes(q.trim().toLowerCase())
  })

  // Defaults to reviewer. The old default read Planning Center's `permissions`
  // field, but that describes who may edit a schedule — the office manager
  // scored 'admin' and the worship leader didn't. A position on a music team
  // says nothing about which Beseter seat someone should hold, so it no longer
  // guesses.
  const seatFor = (p) => rowSeat[p.id] ?? 'reviewer'

  const inviteRow = async (p) => {
    const seat = seatFor(p)
    setRowStatus((s) => ({ ...s, [p.id]: { busy: true } }))

    const [{ data: emailData }, { data: invite, error }] = await Promise.all([
      supabase.functions.invoke('pco-list-team', { body: { church_id: churchId, person_id: p.id } }),
      supabase.rpc('create_invite_code', {
        p_kind: 'church_staff', p_church_id: churchId, p_staff_role: seat, p_staff_title: null,
      }),
    ])
    if (error) return setRowStatus((s) => ({ ...s, [p.id]: { error: readableError(error) } }))

    let emailed = false
    if (emailData?.email) {
      const { error: sendErr } = await supabase.functions.invoke('send-staff-invite', {
        body: { code: invite.code, email: emailData.email },
      })
      emailed = !sendErr
    }
    setRowStatus((s) => ({
      ...s, [p.id]: { done: true, emailed, url: buildInviteUrl(invite.code), code: invite.code },
    }))
    if (!emailed) setViewing({ name: p.name, seat, code: invite.code })
  }

  const send = async () => {
    setBusy(true); setErr(null)
    const { data, error } = await supabase.rpc('create_invite_code', {
      p_kind: 'church_staff', p_church_id: churchId, p_staff_role: role,
      p_staff_title: title.trim() || null,
    })
    setBusy(false)
    if (error) return setErr(readableError(error))
    setCode(data)
  }

  const reset = () => {
    setOpen(false); setCode(null); setTitle(''); setRole('reviewer'); setErr(null)
    setPicking(false); setRoster(null); setRosterErr(null); setRowSeat({}); setRowStatus({})
    setManualOpen(false)
  }

  if (!open) {
    return (
      /* Two ways in, in the same group as the people they add. */
      <div className="rows">
        {pcoConnected && (
          <button className="action" onClick={() => setOpen(true)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <PcoIcon size={19} />
              <span>Add team from Planning Center
                <small>Pull in people already set up there — you pick each person&rsquo;s seat.</small></span>
            </span>
            <span className="ar">›</span>
          </button>
        )}
        <button className="action" onClick={() => { setOpen(true); setManualOpen(true) }}>
          <span>Invite someone yourself
            <small>A writer from your church, a reviewer, or another admin — get a link or QR code to send them.</small></span>
          <span className="ar">›</span>
        </button>
      </div>
    )
  }

  if (code) {
    return (
      <div className="tile" style={{ marginTop: 10, maxWidth: 460 }}>
        <p className="small" style={{ marginBottom: 10 }}>
          Send this to whoever you're bringing on as {SEAT_PHRASE[role]}.
          The seat is theirs the moment they open it and sign up or sign in.
        </p>
        <InvitePanel code={code.code} kind="church_staff" label={churchName} />
        <button className="btn-plain" style={{ marginTop: 10 }} onClick={reset}>Done</button>
      </div>
    )
  }

  const rosterCaption = pcoConnected && roster !== null && roster.length > 0 && (
    <p className="small" style={{ marginBottom: 10 }}>
      Everyone on the teams you picked. Choose a seat per person and invite them straight
      from the list.
    </p>
  )

  /** Step one: which teams. Nothing about any person is fetched until this is
   *  answered, which is the whole point of asking it first. */
  const teamPicker = !manualOpen && pcoConnected && roster === null && teams !== null && (
    <div style={{ marginBottom: 18 }}>
      {teams.length === 0 ? (
        <p className="small">No teams in your Planning Center Services account yet.</p>
      ) : (
        <>
          <p className="small" style={{ marginBottom: 10 }}>
            Pick your music teams. Beseter only ever loads people from the teams you tick —
            your prayer, cafe and security teams stay out of it.
          </p>
          {Object.entries(teamsByType).map(([type, list]) => (
            <div key={type} style={{ marginBottom: 12 }}>
              <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>{type}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {list.map((t) => {
                  const on = chosenTeams.includes(t.id)
                  return (
                    <button
                      key={t.id}
                      className={on ? 'pf-btn pf-btn-solid' : 'pf-btn'}
                      aria-pressed={on}
                      onClick={() => toggleTeam(t.id)}
                    >
                      {on ? '✓ ' : ''}{t.name}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          <button
            className="btn btn-primary"
            style={{ marginTop: 6 }}
            disabled={chosenTeams.length === 0 || picking}
            onClick={() => loadRoster(chosenTeams)}
          >
            {picking ? 'Loading…' : `Show people (${chosenTeams.length} team${chosenTeams.length === 1 ? '' : 's'})`}
          </button>
        </>
      )}
    </div>
  )

  const rowAction = (p) => {
    const status = rowStatus[p.id] || {}
    if (status.done) {
      const openView = () => setViewing({ name: p.name, seat: seatFor(p), code: status.code })
      return status.emailed ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span className="pf-pill pf-pill-ok">Emailed</span>
          <button className="btn-plain" style={{ padding: 0, fontSize: 12.5 }} onClick={openView}>
            View invite
          </button>
        </span>
      ) : (
        <button className="btn-plain" style={{ padding: 0, fontSize: 12.5 }} onClick={openView}>
          Share invite
        </button>
      )
    }
    return (
      <button className={isPhone ? 'btn btn-quiet' : 'pf-btn pf-btn-solid'} disabled={status.busy}
              onClick={() => inviteRow(p)} style={isPhone ? { width: 'auto', padding: '0 16px' } : undefined}>
        {status.busy ? 'Inviting…' : 'Invite'}
      </button>
    )
  }

  return (
    <div className="tile" style={{ marginTop: 10 }}>
      {pcoConnected && (
        <div className="seg" role="group" aria-label="How to add people" style={{ marginBottom: 16 }}>
          <button className={`seg-b${!manualOpen ? ' on' : ''}`} onClick={() => setManualOpen(false)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <PcoIcon size={14} /> Planning Center
          </button>
          <button className={`seg-b${manualOpen ? ' on' : ''}`} onClick={() => setManualOpen(true)}>
            Manual
          </button>
        </div>
      )}

      {!manualOpen && pcoConnected && picking && teams === null && (
        <p className="small">Loading your Planning Center teams…</p>
      )}
      {!manualOpen && teamsErr && <p className="err">{teamsErr}</p>}
      {teamPicker}
      {!manualOpen && rosterErr && <p className="err">{rosterErr}</p>}

      {!manualOpen && roster !== null && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <button className="btn-plain" style={{ padding: 0 }}
                  onClick={() => { setRoster(null); setQ('') }}>
            ← Change teams
          </button>
          {roster.length > 0 && (
            <input
              className="input"
              style={{ flex: '1 1 200px', maxWidth: 280 }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or position"
              aria-label="Search the roster"
            />
          )}
        </div>
      )}

      {!manualOpen && roster !== null && roster.length === 0 && (
        <p className="small">Nobody on those teams yet.</p>
      )}
      {!manualOpen && roster !== null && roster.length > 0 && visibleRoster.length === 0 && (
        <p className="small">Nobody matches “{q}”.</p>
      )}

      {!manualOpen && visibleRoster.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          {rosterCaption}
          {isPhone ? (
            <div className="stack">
              {visibleRoster.map((p) => (
                <div key={p.id} className="tile" style={{ margin: 0 }}>
                  <div className="row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {p.positions?.length > 0 && <div className="small">{p.positions.join(' · ')}</div>}
                    </div>
                    {rowAction(p)}
                  </div>
                  <select className="input" style={{ marginTop: 10 }} value={seatFor(p)}
                          disabled={rowStatus[p.id]?.busy || rowStatus[p.id]?.done}
                          onChange={(e) => setRowSeat((s) => ({ ...s, [p.id]: e.target.value }))}>
                    {SEATS.map(([v, , long]) => <option key={v} value={v}>{long}</option>)}
                  </select>
                  {rowStatus[p.id]?.error && <p className="err">{rowStatus[p.id].error}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="pf-table">
                <thead>
                  <tr><th>Name</th><th>Position</th><th>Seat</th><th>Invite</th></tr>
                </thead>
                <tbody>
                  {visibleRoster.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{p.positions?.length > 0
                        ? p.positions.map((pos) => <span key={pos} className="pf-pill" style={{ marginRight: 4 }}>{pos}</span>)
                        : <span className="pf-td-dim">—</span>}</td>
                      <td>
                        <select className="pf-btn" value={seatFor(p)}
                                disabled={rowStatus[p.id]?.busy || rowStatus[p.id]?.done}
                                onChange={(e) => setRowSeat((s) => ({ ...s, [p.id]: e.target.value }))}
                                aria-label={`Seat for ${p.name}`}>
                          {SEATS.map(([v, short]) => <option key={v} value={v}>{short}</option>)}
                        </select>
                      </td>
                      <td>
                        {rowAction(p)}
                        {rowStatus[p.id]?.error && <div className="err">{rowStatus[p.id].error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {manualOpen && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <Field label="Seat">
              <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
                {SEATS.map(([v, , long]) => <option key={v} value={v}>{long}</option>)}
              </select>
            </Field>
            <Field label="Job title (optional)">
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="Worship Pastor" />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button className="btn btn-primary" disabled={busy} onClick={send} style={{ width: 'auto', padding: '0 20px' }}>
              {busy ? 'Creating…' : 'Create invite'}
            </button>
          </div>
          {err && <p className="err">{err}</p>}
        </div>
      )}

      <button className="btn-plain" style={{ marginTop: 12 }} onClick={reset}>Close</button>

      <dialog ref={shareDlg} className="sheet" onClose={() => setViewing(null)}
              onCancel={(e) => { e.preventDefault(); setViewing(null) }}>
        {viewing && (
          <div className="sheet-body">
            <h2 className="q">Invite {viewing.name}</h2>
            <p className="sub">
              {churchName} is passionate about original songwriting, and uses Beseter to manage
              the process from first draft through to Sunday. {viewing.name} has been invited as{' '}
              {SEAT_PHRASE[viewing.seat]} — send this however
              reaches them fastest.
            </p>
            <InvitePanel code={viewing.code} kind="church_staff" label={churchName} />
            <button className="btn-plain" style={{ marginTop: 10 }} onClick={() => setViewing(null)}>
              Done
            </button>
          </div>
        )}
      </dialog>
    </div>
  )
}

export default function People() {
  const { church } = useSession()
  const { device } = usePlatform()
  const nav = useNavigate()
  const [rows, setRows] = useState(null)
  const [open, setOpen] = useState(null)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    if (!church) return
    const { data, error } = await supabase
      .from('church_members')
      // church_members points at users twice (user_id, verified_by_pastor_id),
      // so the embed has to name the constraint.
      .select('*, users!church_members_user_id_fkey(id, first_name, last_name, email)')
      .eq('church_id', church.id)
      .order('created_at')
    if (error) setErr(readableError(error))
    setRows(data ?? [])
  }, [church])

  useEffect(() => { load() }, [load])

  const patch = async (id, changes) => {
    setErr(null)
    const { error } = await supabase.from('church_members').update(changes).eq('id', id)
    if (error) return setErr(readableError(error))
    load()
  }

  const isPhone = device === 'phone'
  if (!church || rows === null) {
    return isPhone
      ? <Shell><Spinner /></Shell>
      : <PortalFrame title="People"><Spinner /></PortalFrame>
  }

  const pending = rows.filter((r) => r.membership_status === 'pending')
  const staff = rows.filter((r) => r.membership_status === 'approved' && r.role !== 'songwriter')
  const writers = rows.filter((r) => r.membership_status === 'approved' && r.role === 'songwriter')
  const name = (r) => [r.users?.first_name, r.users?.last_name].filter(Boolean).join(' ') || r.users?.email || '—'

  /* ---------- desktop / tablet ----------
     One table, everyone in it. The phone version splits into three stacked
     groups because a phone can only show one thing at a time; on a desktop the
     whole church fits at once, and sorting pending to the top is enough to say
     who needs you. Status and Verified stay separate columns on purpose —
     approved is access, vouched is the church backing them as a writer. */
  if (!isPhone) {
    const ordered = [...pending, ...staff, ...writers]
    const stats = [
      ['Approved', staff.length + writers.length],
      ['Pending', pending.length],
      ['Staff seats', staff.length],
      ['Songwriters', writers.length],
    ]
    return (
      <PortalFrame title="People" sub="Everyone with access to this church, and what they can do.">
      <div className="pf-cols pf-cols-2">
      <div>
        <div className="pf-card">
          <table className="pf-table">
            <thead>
              <tr>
                <th>Name</th><th>Seat</th><th>Status</th><th>Verified</th>
                <th>Capabilities</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <span className={`pf-song-ava ${avatarTone(r.user_id || r.id)}`}>{initials(name(r))}</span>
                      <span>
                        <span style={{ display: 'block', fontWeight: 600 }}>{name(r)}</span>
                        <span className="pf-song-m">{r.staff_title || r.users?.email}</span>
                      </span>
                    </span>
                  </td>
                  <td>
                    <select className="pf-btn" value={r.role}
                            onChange={(e) => patch(r.id, { role: e.target.value })}
                            aria-label={`Seat for ${name(r)}`}>
                      {SEATS.map(([v, short]) => <option key={v} value={v}>{short}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className={`pf-pill ${r.membership_status === 'approved' ? 'pf-pill-ok'
                      : r.membership_status === 'pending' ? 'pf-pill-warn' : 'pf-pill-ready'}`}>
                      {r.membership_status}
                    </span>
                  </td>
                  <td>
                    {r.verified
                      ? <span className="pf-pill pf-pill-ok">Vouched</span>
                      : <span className="pf-td-dim">—</span>}
                  </td>
                  <td>
                    {r.role === 'reviewer' ? (
                      <span className="pf-flags">
                        {FLAGS.map(([k, label]) => (
                          <button key={k} className={`pf-flag${r[k] ? '' : ' off'}`}
                                  onClick={() => patch(r.id, { [k]: !r[k] })}
                                  title={`${r[k] ? 'Turn off' : 'Turn on'} — ${label}`}>
                            {label}
                          </button>
                        ))}
                      </span>
                    ) : <span className="pf-td-dim">—</span>}
                  </td>
                  <td>
                    <span className="pf-rowbtns">
                      {r.membership_status === 'pending' && <>
                        <button className="pf-btn pf-btn-solid"
                                onClick={() => patch(r.id, { membership_status: 'approved' })}>Approve</button>
                        <button className="pf-btn pf-btn-quiet"
                                onClick={() => patch(r.id, { membership_status: 'rejected' })}>Decline</button>
                      </>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ordered.length === 0 && <p className="pf-empty">Nobody here yet.</p>}
        </div>
        <p className="pf-empty" style={{ margin: '12px 0 0' }}>
          A person invited here still signs in with their own email. Add them, then tell them to sign up.
        </p>

        <StaffInvite churchId={church.id} churchName={church.name} />
        {err && <p className="err">{err}</p>}
      </div>

      <div>
        <div className="pf-accent">This month</div>
        <div className="pf-stats">
          {stats.map(([label, n]) => (
            <div key={label} className="pf-stat">
              <div className="pf-stat-n">{n}</div>
              <div className="pf-stat-l">{label}</div>
            </div>
          ))}
        </div>
      </div>
      </div>
      </PortalFrame>
    )
  }

  /* ---------- phone ---------- */
  return (
    <Shell>
      <div className="eyebrow">Administrator</div>
      <h2 className="q">{church.name}</h2>
      <p className="sub">People and setup. Songs are the reviewers' side — you never see a review screen.</p>
      <Sill />

      <div className="rows">
        <button className="action" onClick={() => nav('/admin/process')}>
          <span>Your review process<small>Steps and defaults</small></span>
          <span className="ar">›</span>
        </button>
        <button className="action" onClick={() => nav('/admin/profile')}>
          <span>Your church page<small>Songs, writers and links you show off</small></span>
          <span className="ar">›</span>
        </button>
      </div>

      {/* The same card the reviewer's "Needs you" uses. Both are a person
          waiting on the person reading the screen, and they were drawn two
          different ways — this one as .cosign, whose lit border means "has
          signed" on the co-writer screen and means nothing here. */}
      {pending.length > 0 && <>
        <h3 className="sechead">Waiting on you</h3>
        {pending.map((r) => (
          <div key={r.id} className="queue">
            <div className="ava">{initials(name(r))}</div>
            <span className="queue-t">
              {name(r)}
              <small>{r.users?.email} · asked to join</small>
            </span>
            <div className="queue-acts">
              <button className="iconbtn" onClick={() => patch(r.id, { membership_status: 'approved' })}>Approve</button>
              <button className="iconbtn" onClick={() => patch(r.id, { membership_status: 'rejected' })}>Not yet</button>
            </div>
          </div>
        ))}
      </>}

      <h3 className="sechead">Staff</h3>
      <StaffInvite churchId={church.id} churchName={church.name} />
      {staff.length === 0 && <p className="small" style={{ marginTop: 10 }}>No reviewers yet. Add one so songs have somewhere to land.</p>}
      <div className="rows">
      {staff.map((r) => (
        <div key={r.id} className="rowwrap">
          <button className="action" onClick={() => setOpen(open === r.id ? null : r.id)}>
            <span>{name(r)}
              <small>{r.staff_title || ROLE_LABEL[r.role]} · {ROLE_LABEL[r.role]}</small></span>
            <span className={`ar${open === r.id ? ' down' : ''}`}>›</span>
          </button>

          {open === r.id && (
            <div className="rowbody">
              <label className="label">Job title</label>
              <input className="input" defaultValue={r.staff_title || ''}
                     onBlur={(e) => patch(r.id, { staff_title: e.target.value })} />

              <label className="label" style={{ marginTop: 14 }}>Seat</label>
              <select className="input" value={r.role}
                      onChange={(e) => patch(r.id, { role: e.target.value })}>
                {SEATS.map(([v, , long]) => <option key={v} value={v}>{long}</option>)}
              </select>

              {r.role === 'reviewer' && <div style={{ marginTop: 10 }}>
                <ToggleRow title="Can leave a note" hint="Free text back to the writer"
                  on={r.can_leave_freetext} onChange={(v) => patch(r.id, { can_leave_freetext: v })} />
                <ToggleRow title="Can use set responses" hint="The warm canned replies"
                  on={r.can_use_canned_responses} onChange={(v) => patch(r.id, { can_use_canned_responses: v })} />
                <ToggleRow title="Can set meetings" hint="Offer a time to talk it through"
                  on={r.can_set_meetings} onChange={(v) => patch(r.id, { can_set_meetings: v })} />
                <ToggleRow title="Can approve songwriters" hint="Wave new writers into the church"
                  on={r.can_approve_members} onChange={(v) => patch(r.id, { can_approve_members: v })} />
              </div>}
            </div>
          )}
        </div>
      ))}
      </div>

      <h3 className="sechead">Songwriters</h3>
      {writers.length === 0 && <p className="small">Nobody yet.</p>}
      <div className="rows">
      {writers.map((r) => (
        <div key={r.id} className="methodrow">
          <div className="ava">{initials(name(r))}</div>
          <span className="mt">{name(r)}<small>{r.users?.email}</small></span>
          <span className="badge">Approved</span>
        </div>
      ))}
      </div>

      <p className="small" style={{ marginTop: 24 }}>
        A person invited here still signs in with their own email. Add them, then tell them to sign up.
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
